import * as React from "react";
import { connect } from "react-redux";
import { Loader, Segment } from "semantic-ui-react";
import { createValidator, validators } from "not-valid";
import { uniq, flatten, values } from "lodash";

import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { RadioButtonQuestion, RadioButton } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { ReferralFormState } from "@teamprevent/common/questionnaires/components/referralForm/models";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { getMedicalTypeName } from "@teamprevent/common/customMedicalTypes/model";
import {
    QuestionnairesState,
    AnsweredQuestion,
    AnsweredQuestionWithId,
    OrgLevel,
    QuestionnaireTypes,
    QuestionAnswer,
    DropdownOption
} from "@teamprevent/common/questionnaires/model";
import { updateQuestion, openSection, clearQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { CurrentUser } from "@teamprevent/common/model";
import {
    QuestionnaireConfig,
    AdditionalQuestionsSectionId,
    CustomQuestionConfig,
    QuestionConfig,
    renderAdditionalQuestions,
    isQuestionMandatory
} from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { dateOfBirthInRange } from "@teamprevent/common/validators/dateOfBirthInRange";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import * as moment from "moment";

import * as questionIds from "../../questionnaires/questionnaireConfig/ppqManagerSection/questionIds";
import { isCustomerUser } from "../../auth/roles";
import { RecruitmentManager } from "../../recruitmentManagers/models";
import { WorkGroup } from "../../workGroup/model";
import { N_A, N_A_OPTION } from "../models";
import { CustomMedicalType } from "../../healthSurveillance/models";
import { purchaseOrderNumberValidator } from "../../ohCase/validators/purchaseOrderNumberValidator";

export const QUESTIONNAIRE_KEY = "PpqManagerSection";

interface OwnProps {
    questionnaireConfig: QuestionnaireConfig;
    readonly: boolean;
    hideDividers?: boolean;
    hideEmployeeDetails?: boolean;
    sectionsOpen?: boolean;
    candidateDetailsTitle?: string;
    submitQuestionnaire?: (answers: AnsweredQuestionWithId[], questionnaireKey: string) => Promise<boolean>;
    hideSubmitButton?: boolean;
    loadOrgStructure?: (parentOrgStructureLevelId: string) => void;
    showCovid19Questions?: boolean;
    customerWorkGroups: WorkGroup[];
    customMedicalTypes: CustomMedicalType[];
}

interface StateProps {
    currentUser: CurrentUser;
    orgLevels: OrgLevel[];
    recruitmentManagers: RecruitmentManager[];
    questions: AnsweredQuestionWithId[];
}

interface DispatchProps {
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    openFirstSection: () => void;
    clearQuestionnaire: () => void;
}

interface OwnState {
    selectedOrgStructureLevelIds: string[];
}

export const detailsOfCandidateSectionId = "DetailsOfCandidate";

const anticipatedStartDateYearInRange = createValidator(
    (value: moment.Moment) => value.isBetween(moment().subtract(2, "years"), moment().add(2, "years"), "year", "[]"),
    "Please enter a valid date");

class PpqManagerSectionUnconnected extends React.Component<OwnProps & StateProps & DispatchProps, OwnState> {
    private readonly orgLevelQuestionId = "OrgLevel";
    private canSubmitFormWhenOrgLevelsAreDisabled = false;

    private validateConfirmEmailAddress =
        createValidator((value) => this.getAnswerToQuestionWithId("PersonalEmailAddress") === value, "Email addresses do not match");

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);
        this.state = { selectedOrgStructureLevelIds: [] };
        this.loadQuestionnaire = this.loadQuestionnaire.bind(this);
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
    }

    public componentDidMount() {
        this.props.openFirstSection();
        this.canSubmitFormWhenOrgLevelsAreDisabled = getFeatureToggles()[FeatureToggleNames.CanSubmitFormWhenOrgLevelsAreDisabled];
    }

    private getQuestionMandatoryConfig(questionId: string, defaultValue: boolean = true): boolean {
        return isQuestionMandatory(this.props.questionnaireConfig, questionId, defaultValue);
    }

    public render() {
        if (!this.props.currentUser.id && !this.props.readonly) {
            return <Loader active />;
        }

        const customerWorkGroups: DropdownOption[] = [
            N_A_OPTION,
            ...(this.props.customerWorkGroups ?? []).sort((a, b) => a.name.localeCompare(b.name)).map(wg => ({ key: wg.id, value: wg.id, text: wg.name }))
        ];

        const workGroupsQuestionAnswer = (this.props.questions.find(q => q.id === questionIds.WorkGroups)?.answer ?? []) as string[];
        const medicalTypeIds = this.getDistinctMedicals(workGroupsQuestionAnswer);
        const medicalTypeNames = medicalTypeIds.map(mt => getMedicalTypeName(this.props.customMedicalTypes, mt));
        const workGroupsQuestionConfig = this.getQuestionConfig(questionIds.WorkGroups);
        const hideWorkGroupsQuestion = workGroupsQuestionConfig.disabled;

        return (
            <OldProgressBarQuestionnaireContainer
                loadQuestionnaire={this.loadQuestionnaire}
                submitQuestionnaire={this.submitQuestionnaire}
                readonly={this.props.readonly}
                questionnaireKey={QUESTIONNAIRE_KEY}
                hideSubmitButton={this.props.hideSubmitButton}
                questionnaireType={QuestionnaireTypes.Other}
            >
                {this.props.hideEmployeeDetails ||
                    <OldProgressBarQuestionnaireSection
                        title={this.props.candidateDetailsTitle || "Details of Candidate"}
                        id={detailsOfCandidateSectionId}
                        active={this.props.sectionsOpen}
                    >
                        {isCustomerUser(this.props.currentUser) || this.props.readonly ?
                            <>
                                <TextQuestion
                                    questionConfig={({ id: "LineManagerId", questionText: "Line Manager Id" })}
                                    hidden
                                    readonly
                                    answer={this.props.currentUser.id}
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerName)}
                                    answer={this.props.currentUser.name}
                                    readonly
                                    hidden
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerEmail)}
                                    hidden
                                    readonly
                                    answer={this.props.currentUser.email}
                                />
                            </> :
                            <>
                                <DropdownQuestion
                                    questionConfig={({ ...this.getQuestionConfig(questionIds.LineManagerName), id: "LineManagerId" })}
                                    options={this.props.recruitmentManagers &&
                                        this.props.recruitmentManagers.map(recruitmentManagers =>
                                            ({
                                                text: recruitmentManagers.name,
                                                value: recruitmentManagers.id
                                            })
                                        )}
                                    mandatory
                                    readonly={this.props.readonly}
                                    onAnswerChange={this.changeLineManager}
                                    hidden={this.props.readonly}
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerName)}
                                    readonly={this.props.readonly}
                                    hidden={!this.props.readonly}
                                    mandatory
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerEmail)}
                                    readonly
                                />
                            </>
                        }
                        <TextQuestion
                            hideDivider
                            questionConfig={this.getQuestionConfig(questionIds.PurchaseOrderNumber)}
                            readonly={this.props.readonly}
                            mandatory={this.getQuestionMandatoryConfig(questionIds.PurchaseOrderNumber, false)}
                            validators={[purchaseOrderNumberValidator]}
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.ReferredTitle)}
                            readonly={this.props.readonly}
                            hideDivider
                            validators={[validators.validLength({ max: 25, message: "Maximum of 25 characters only" })]}
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.ReferredFirstName)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.ReferredLastName)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <OldDateQuestion
                            questionConfig={this.getQuestionConfig(questionIds.ReferredDob)}
                            readonly={this.props.readonly}
                            mandatory={this.getQuestionMandatoryConfig(questionIds.ReferredDob)}
                            hideDivider
                            validators={[dateOfBirthInRange]}
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.PersonalEmailAddress)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.ConfirmPersonalEmailAddress)}
                            readonly={this.props.readonly}
                            mandatory
                            validators={[this.validateConfirmEmailAddress]}
                            validationDependencies={[this.getAnswerToQuestionWithId("PersonalEmailAddress") as string]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.Postcode)}
                            readonly={this.props.readonly}
                            mandatory={this.getQuestionMandatoryConfig(questionIds.Postcode)}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.JobTitle)}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider
                        />
                        <RadioButtonQuestion
                            questionConfig={this.getQuestionConfig(questionIds.FullPartTime)}
                            readonly={this.props.readonly}
                            hideDivider={this.props.hideDividers}
                        >
                            <RadioButton label="Part-Time" value="Part-Time" />
                            <RadioButton label="Full-Time" value="Full-Time" />
                        </RadioButtonQuestion>
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.EmployeeId)}
                            readonly={this.props.readonly}
                            mandatory={this.getQuestionMandatoryConfig(questionIds.EmployeeId)}
                            hideDivider
                            validators={[ validators.validLength({ max: 20 }) ]}
                        />
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("AnticipatedStartDate")) &&
                            <OldDateQuestion
                                questionConfig={this.getQuestionConfig(questionIds.AnticipatedStartDate)}
                                readonly={this.props.readonly}
                                hideDivider
                                validators={[anticipatedStartDateYearInRange]}
                            />
                        }
                        <DropdownQuestion
                            mandatory
                            questionConfig={{ ...this.getQuestionConfig(questionIds.OrgLevel1Name), id: "OrgLevel1Id" }}
                            options={this.props.orgLevels &&
                                this.props.orgLevels.filter(orgLevel => !orgLevel.parentId)
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => this.changeOrgStructureLevel(0, orgStructureId as string)}
                            search
                            readonly={this.props.readonly}
                            hidden={this.props.readonly}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.OrgLevel1Name)}
                            hidden={!this.props.readonly || !this.getAnswerToQuestionWithId("OrgLevel1Name")}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel2Name), id: "OrgLevel2Id" })}
                            options={this.props.orgLevels && this.state.selectedOrgStructureLevelIds[0] &&
                                this.props.orgLevels.filter(orgLevel => orgLevel.parentId === this.state.selectedOrgStructureLevelIds[0])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => this.changeOrgStructureLevel(1, orgStructureId as string)}
                            search
                            readonly={this.props.readonly}
                            hidden={this.props.readonly || !this.state.selectedOrgStructureLevelIds[0]}
                            mandatory={this.getQuestionMandatoryConfig(questionIds.OrgLevel2Name, false)}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.OrgLevel2Name)}
                            hidden={!this.props.readonly || !this.getAnswerToQuestionWithId("OrgLevel2Name")}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel3Name), id: "OrgLevel3Id" })}
                            options={this.props.orgLevels && this.state.selectedOrgStructureLevelIds[1] &&
                                this.props.orgLevels.filter(orgLevel => orgLevel.parentId === this.state.selectedOrgStructureLevelIds[1])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => this.changeOrgStructureLevel(2, orgStructureId as string)}
                            search
                            readonly={this.props.readonly}
                            hidden={this.props.readonly || !this.state.selectedOrgStructureLevelIds[1]}
                            mandatory={this.getQuestionMandatoryConfig(questionIds.OrgLevel3Name, false)}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.OrgLevel3Name)}
                            hidden={!this.props.readonly || !this.getAnswerToQuestionWithId("OrgLevel3Name")}
                            readonly
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel4Name), id: "OrgLevel4Id" })}
                            options={this.props.orgLevels && this.state.selectedOrgStructureLevelIds[2] &&
                                this.props.orgLevels.filter(orgLevel => orgLevel.parentId === this.state.selectedOrgStructureLevelIds[2])
                                    .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                            // eslint-disable-next-line react/jsx-no-bind
                            onAnswerChange={orgStructureId => this.changeOrgStructureLevel(3, orgStructureId as string)}
                            search
                            readonly={this.props.readonly}
                            hidden={this.props.readonly || !this.state.selectedOrgStructureLevelIds[2]}
                            hideDivider
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.OrgLevel4Name)}
                            hidden={!this.props.readonly || !this.getAnswerToQuestionWithId("OrgLevel4Name")}
                            readonly
                            hideDivider
                        />
                        {!hideWorkGroupsQuestion &&
                            <>
                                <DropdownQuestion
                                    questionConfig={workGroupsQuestionConfig}
                                    options={customerWorkGroups}
                                    onAnswerChange={this.onWorkGroupsChanged}
                                    search
                                    readonly={this.props.readonly}
                                    hideDivider
                                    mandatory
                                    multiple
                                />
                                {medicalTypeIds.length > 0 &&
                                    <Segment>
                                        <p><strong>Medicals:</strong></p>
                                        {medicalTypeNames.sort().join(", ")}
                                    </Segment>
                                }
                            </>
                        }
                    </OldProgressBarQuestionnaireSection>}
                <OldProgressBarQuestionnaireSection
                    title="Hazard/Risk Associated with the post"
                    id="RisksAssociatedWithThePost"
                    active={this.props.sectionsOpen}>
                    <div className="checkbox-grid">
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("EvidenceOfCovid19VaccinationStatusMandatory")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.EvidenceOfCovid19VaccinationStatusMandatory)}
                                value="Evidence of COVID-19 Vaccination status is mandatory for role"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("ExposureProneProcedure")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ExposureProneProcedure)}
                                value="Exposure Prone Procedure (EPP) Work"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("RegularPatientContact")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.RegularPatientContact)}
                                value="Direct Patient Care or Regular Patient Contact / Contact with Clinical Specimens or Pathogens / Pathology Work"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("ProlongedRepetitiveMovements")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ProlongedRepetitiveMovements)}
                                value="Prolonged Repetitive Movements / Actions"
                                readonly={this.props.readonly}
                            />
                        }

                        {(!this.props.readonly || this.getAnswerToQuestionWithId("HeavyLoads")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.HeavyLoads)}
                                value="Moving or handling heavy loads"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingShifts")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingShifts)}
                                value="Working shifts"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingNights")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingNights)}
                                value="Working nights"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingDustFumes")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingDustFumes)}
                                value="Working with dust or fumes"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingIrritants")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingIrritants)}
                                value="Working with skin irritants / sensitisers"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingChemicals")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingChemicals)}
                                value="Working with chemicals (industrial or cleaning)"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingConfinedSpace")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingConfinedSpace)}
                                value="Working in a confined space"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingVibratingMachinery")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingVibratingMachinery)}
                                value="Working with vibrating machinery / tools"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingHeights")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingHeights)}
                                value="Working at Heights "
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingWithSewage")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingWithSewage)}
                                value="Working with Sewage"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("LoneWorking")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.LoneWorking)}
                                value="Lone Working"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingOutside")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingOutside)}
                                value="Working Outside"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("ExposureToNoiseLevels")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ExposureToNoiseLevels)}
                                value="Exposure to Noise Levels (>80dbA)"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkWithVdus")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkWithVdus)}
                                value="Work with VDUs (>5hrs per week)"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("FoodHandling")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.FoodHandling)}
                                value="Food Handling"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("DrivingDuties")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.DrivingDuties)}
                                value="Driving Duties"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("DrivingLGV")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.DrivingLGV)}
                                value="Driving LGV or PCV’s"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("DrivingForkliftTrucks")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.DrivingForkliftTrucks)}
                                value="Driving forklift Trucks"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("ContactWithLatex")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ContactWithLatex)}
                                value="Contact with latex"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("ContactWithCytotoxics")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ContactWithCytotoxics)}
                                value="Contact with cytotoxics"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("WorkingWithChildren")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.WorkingWithChildren)}
                                value="Working with children"
                                readonly={this.props.readonly}
                            />
                        }
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("ExposureToChallengingOrAggressiveBehaviour")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.ExposureToChallengingOrAggressiveBehaviour)}
                                value="Exposure to persons with challenging or aggressive behaviour"
                                readonly={this.props.readonly}
                            />
                        }
                    </div>
                    <div className="checkbox-grid">
                        {(!this.props.readonly || this.getAnswerToQuestionWithId("VocationalDriving")) &&
                            <CheckboxQuestion
                                questionConfig={this.getQuestionConfig(questionIds.VocationalDriving)}
                                value="Vocational Driving"
                                readonly={this.props.readonly}
                            />
                        }
                        {this.getAnswerToQuestionWithId("VocationalDriving") &&
                            <TextQuestion
                                questionConfig={this.getQuestionConfig(questionIds.VocationalDrivingLevel)}
                                readonly={this.props.readonly}
                            />}
                    </div>
                    <TextQuestion
                        questionConfig={this.getQuestionConfig(questionIds.ConfirmJobFamily)}
                        readonly={this.props.readonly}
                        hideDivider
                    />
                    <TextQuestion
                        questionConfig={this.getQuestionConfig(questionIds.OtherHealthSurveillanceChecks)}
                        readonly={this.props.readonly}
                        hideDivider
                    />
                    <br />
                    {((!this.props.readonly && this.props.showCovid19Questions) || this.getAnswerToQuestionWithId("Covid19RiskAssessment")) &&
                        <CheckboxQuestion
                            questionConfig={this.getQuestionConfig(questionIds.Covid19RiskAssessment)}
                            value="Ask the Candidate to complete a COVID-19 Risk Assessment"
                            readonly={this.props.readonly}
                        />
                    }
                </OldProgressBarQuestionnaireSection>
                <OldProgressBarQuestionnaireSection id={AdditionalQuestionsSectionId} title="Additional Questions">
                    {renderAdditionalQuestions(this.props.questionnaireConfig.additionalQuestions, this.props.readonly, false)}
                </OldProgressBarQuestionnaireSection>
            </OldProgressBarQuestionnaireContainer>
        );
    }

    // The loading of this questionnaire is controlled by the parent component as the question config array might come from various places.
    // eslint-disable-next-line no-empty
    public loadQuestionnaire() {
        // Do nothing
    }

    public async submitQuestionnaire(answers: AnsweredQuestionWithId[]): Promise<boolean> {

        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredTitle, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredFirstName, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredLastName, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.PersonalEmailAddress, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ConfirmPersonalEmailAddress, answers);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.JobTitle, answers);

        await this.props.submitQuestionnaire(answers, QUESTIONNAIRE_KEY);

        return true;
    }

    private trimAnswerAndUpdateEmployeeQuestion(questionId: string, questions: AnsweredQuestionWithId[]) {

        const stateQuestion = this.props.questions.find(q => q.id === questionId);

        if (stateQuestion) {
            this.props.updateAnswer({
                sectionId: detailsOfCandidateSectionId,
                id: stateQuestion.id,
                question: { ...stateQuestion, answer: stateQuestion.answer?.toString()?.trim() }
            });
        }

        const question = questions.find(q => q.id === questionId);

        if (question) {
            question.answer = question.answer?.toString()?.trim();
        }
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.questions.find(question => question.id === id) && this.props.questions.find(question => question.id === id).answer;
    }

    private changeOrgStructureLevel(level: number, selectedId: string) {
        this.setState({ selectedOrgStructureLevelIds: [...this.state.selectedOrgStructureLevelIds.slice(0, level), selectedId] });
        const selectedName = this.props.orgLevels?.find(orgLevel => orgLevel.id === selectedId)?.name;
        const thisOrgLevelNameQuestion = this.props.questions.find(question => question.id === `${this.orgLevelQuestionId}${level + 1}Name`);

        if (!this.canSubmitFormWhenOrgLevelsAreDisabled || thisOrgLevelNameQuestion) {
            this.props.updateAnswer({
                sectionId: detailsOfCandidateSectionId,
                id: thisOrgLevelNameQuestion.id,
                question: { ...thisOrgLevelNameQuestion, answer: selectedName }
            });
        }

        for (let i = level + 2; i <= 4; i++) {
            const orgLevelIdQuestion = this.props.questions.find(question => question.id === `${this.orgLevelQuestionId}${i}Id`);

            if (!this.canSubmitFormWhenOrgLevelsAreDisabled || orgLevelIdQuestion) {
                this.props.updateAnswer({
                    sectionId: detailsOfCandidateSectionId,
                    id: orgLevelIdQuestion.id,
                    question: { ...orgLevelIdQuestion, answer: "" }
                });
            }

            const orgLevelNameQuestion = this.props.questions.find(question => question.id === `${this.orgLevelQuestionId}${i}Name`);

            if (!this.canSubmitFormWhenOrgLevelsAreDisabled || orgLevelNameQuestion) {
                this.props.updateAnswer({
                    sectionId: detailsOfCandidateSectionId,
                    id: orgLevelNameQuestion.id,
                    question: { ...orgLevelNameQuestion, answer: "" }
                });
            }
        }
        if (this.props.loadOrgStructure) {
            this.props.loadOrgStructure(selectedId);
        }
    }

    private onWorkGroupsChanged = (answer: QuestionAnswer) => {
        if (Array.isArray(answer)) {
            const newAnswer = (answer ?? []) as string[];
            const workGroupsQuestion = this.props.questions.find(q => q.id === questionIds.WorkGroups);

            const currentAnswer = (workGroupsQuestion.answer ?? []) as string[];

            if (currentAnswer.includes(N_A) && newAnswer.length === 0) {
                this.props.updateAnswer({
                    sectionId: detailsOfCandidateSectionId,
                    id: questionIds.WorkGroups,
                    question: { ...workGroupsQuestion, answer: [], isValid: false }
                });
                return;
            }

            if (currentAnswer.includes(N_A) || (currentAnswer.length === 0 && newAnswer.includes(N_A))) {
                this.props.updateAnswer({
                    sectionId: detailsOfCandidateSectionId,
                    id: questionIds.WorkGroups,
                    question: { ...workGroupsQuestion, answer: [N_A], isValid: true }
                });
                return;
            }

            const newWorkGroups = newAnswer.filter(a => a !== N_A) ?? [];
            const isValid = newWorkGroups.length > 0;
            this.props.updateAnswer({
                sectionId: detailsOfCandidateSectionId,
                id: questionIds.WorkGroups,
                question: { ...workGroupsQuestion, answer: newWorkGroups, isValid }
            });
        }
    };

    private getDistinctMedicals(workGroupsQuestionAnswer: string[]) {
        const selectedWorkGroups = this.props.customerWorkGroups.filter(wg => workGroupsQuestionAnswer.includes(wg.id));
        const medicals = selectedWorkGroups.map(wg => wg.medicals) ?? [];
        if (medicals.length > 0) {
            return uniq(medicals.reduce((prev, next,) => {
                return prev.concat(next);
            })) ?? [];
        }

        return [];
    }

    private getQuestionConfig(questionId: string) {
        const config = this.props.questionnaireConfig.questions.find(c => c.id === questionId);
        if (config) {
            return config;
        }
        return { id: questionId, disabled: true } as QuestionConfig;
    }

    private changeLineManager = (lineManagerId: string) => {
        const newLineManager = this.props.recruitmentManagers.find(lineManager => lineManager.id === lineManagerId);
        // Line manager name and email questions cannot be disabled so will always be a CustomQuestionConfig
        const lineManagerNameQuestionConfig = this.getQuestionConfig(questionIds.LineManagerName) as CustomQuestionConfig;
        const lineManagerEmailQuestionConfig = this.getQuestionConfig(questionIds.LineManagerEmail) as CustomQuestionConfig;
        this.props.updateAnswer({
            sectionId: detailsOfCandidateSectionId, id: questionIds.LineManagerName,
            question: {
                id: lineManagerNameQuestionConfig.id,
                question: lineManagerNameQuestionConfig.questionText,
                answer: newLineManager?.name,
                isValid: true
            }
        });
        this.props.updateAnswer({
            sectionId: detailsOfCandidateSectionId, id: questionIds.LineManagerEmail,
            question: {
                question: lineManagerEmailQuestionConfig.questionText,
                answer: newLineManager?.email,
                isValid: true
            }
        });
    };
}

const mapStateToProps = (state: {
    referralForm: ReferralFormState;
    questionnaires: QuestionnairesState;
    currentUser: CurrentUser;
}): StateProps => {
    return {
        questions: state.questionnaires[QUESTIONNAIRE_KEY] ?
            uniq(flatten(state.questionnaires[QUESTIONNAIRE_KEY].sections.map(section => values(section.questions)))) :
            [],
        orgLevels: state.referralForm && state.referralForm.orgLevels,
        recruitmentManagers: state.referralForm && state.referralForm.recruitmentManagers,
        currentUser: state.currentUser
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })),
    openFirstSection: () => dispatch(openSection({ questionnaireKey: QUESTIONNAIRE_KEY, sectionId: "DetailsOfCandidate" })),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(QUESTIONNAIRE_KEY))
});

export const PpqManagerSection = connect(mapStateToProps, mapDispatchToProps)(PpqManagerSectionUnconnected);
